<script lang="ts" setup>
import { i18n } from '@/i18n';
import Modal from '@/components/modal/Modal.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { computed, reactive, watch } from 'vue';
import MultipleUserSelectInput from '@/corePagesModule/user/SelectInput/MultipleUserSelectInput.vue';

// props
const props = withDefaults(
  defineProps<{
    modalState?: boolean;
    selectInputIndex?: number; // selectInputの排他処理に必要
    showForecastName?: boolean;
  }>(),
  {
    modalState: false,
    selectInputIndex: 0,
    showForecastName: false,
  }
);

// emits
const emits = defineEmits<{
  (e: 'closeModal'): void;
  (e: 'apply', selectedStaffIdList: string[], forecastForReceiptName?: string): void;
}>();

//変数
const t = i18n.t;

const state = reactive<{
  selectedStaffIdList: string[];
  forecastForReceiptName: string;
}>({
  selectedStaffIdList: [],
  forecastForReceiptName: '',
});

/**
 * 適用ボタンをクリックした時の処理
 */
const onClickApply = () => {
  emits('apply', state.selectedStaffIdList, props.showForecastName ? state.forecastForReceiptName : undefined);
};

watch(
  () => props.modalState,
  () => {
    if (!props.modalState) {
      state.selectedStaffIdList = [];
      state.forecastForReceiptName = '';
    }
  }
);

const isDisabledApplyEditButton = computed(() => {
  if (props.showForecastName) {
    return !state.selectedStaffIdList.length || !state.forecastForReceiptName.trim();
  }
  return !state.selectedStaffIdList.length;
});
</script>

<template>
  <Modal
    class="edit-modal-container"
    :modal-size="764"
    :stretch-container="true"
    :scroll-off="true"
    :padding="'16px'"
    :modal-state="modalState"
    :border-radius="'16px'"
    @outside-click="emits('closeModal')"
  >
    <div class="edit-modal-content">
      <div class="edit-modal-content__header header">
        <span class="header__title">
          {{ t('編集') }}
        </span>
        <span class="header__description">
          {{ t('forecastForReceipt_descriptionOfEditForecast') }}
        </span>
      </div>

      <div class="edit-modal-content__main-content main-content">
        <div class="main-content__container">
          <div class="main-content__change-basic-info">
            <div class="main-content__select-input-content select-input-content">
              <MultipleUserSelectInput :select-input-index="selectInputIndex" @update:model-value="state.selectedStaffIdList = $event" />
            </div>
            <div v-if="showForecastName" class="main-content__forecast-name-content forecast-name-content">
              <label class="forecast-name-content__label">{{ t('受領用内示') }}</label>
              <input
                v-model="state.forecastForReceiptName"
                class="forecast-name-content__input"
                type="text"
                :placeholder="t('受領用内示名')"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="edit-modal-content__footer footer">
        <TextButton
          :button-style="'btn-cancel'"
          :size="'btn-md'"
          :on-click="() => emits('closeModal')"
          class="footer__button footer__button--cancel"
        >
          {{ t('キャンセル') }}
        </TextButton>
        <TextButton
          class="footer__button footer__button--confirm"
          :button-style="'btn-primary'"
          :size="'btn-md'"
          :disabled="isDisabledApplyEditButton"
          :on-click="onClickApply"
        >
          {{ t('適用') }}
        </TextButton>
      </div>
    </div>
  </Modal>
</template>

<style lang="scss" scoped>
.edit-modal-content {
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 24px;

  &__header {
    display: grid;
    grid-template-rows: repeat(2, auto);
    gap: 24px;
    align-content: space-between;
    justify-items: center;
  }

  &__footer {
    display: grid;
    grid-template-columns: repeat(2, fit-content(50%));
    padding-top: 16px;
  }
}

.header {
  color: $textDefault;

  &__title {
    font-size: 22px;
    font-weight: 700;
    line-height: 28.6px;
  }

  &__description {
    font-size: 14px;
    line-height: 20.27px;
    font-weight: 400;
  }
}

.main-content {
  background-color: $backgroundDefault;
  border-radius: 8px;

  &__container {
    height: 100%;
    width: 100%;
    padding: 16px;
    display: grid;
    grid-template-rows: auto;
    gap: 16px;
    align-content: start;
  }

  &__change-basic-info {
    display: grid;
    grid-template-columns: repeat(2, auto);
    gap: 16px;
    justify-content: start;
  }

  .select-input-content {
    &__select-input {
      width: 160px;
      height: 32px;
    }
  }

  .forecast-name-content {
    display: grid;
    grid-template-rows: auto auto;
    gap: 0;

    &__label {
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 400;
      line-height: 18.2px;
      color: $textDefault;
      padding: 8px 0;
    }

    &__input {
      width: 100%;
      height: 36px;
      padding: 6px 8px;
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 400;
      line-height: 20.27px;
      color: $textDefault;
      border: 1px solid $borderDefault;
      border-radius: 4px;
      background-color: $semanticWhite;
      box-sizing: border-box;

      &::placeholder {
        color: $textSecondary;
      }

      &:focus {
        outline: none;
        border-color: $semanticPrimary;
        box-shadow: 0px 0px 16px 0px #4dbde999;
      }
    }
  }
}

.footer {
  display: grid;
  grid-template-columns: repeat(2, auto);
  justify-self: center;
  gap: 10px;

  &__button {
    width: 160px;
    height: 40px;
    font-size: 16px;
    line-height: 16px;
    font-weight: 700;

    &--cancel {
      padding: 6px 32px 6px 32px;
    }
    &--confirm {
      padding: 6px 56px 6px 56px;
    }
  }
}
</style>
